import React from 'react';
import Axios from 'axios';
import {
  plato_api_notebook_path,
  plato_api_reading_game_path,
  plato_api_slide_show_path,
  plato_api_slide_group_path,
  plato_api_section_path,
  plato_api_find_exercise_result_for_grading_page_exercise_results_path,
} from 'modules/routes';
import { useQuery } from '@tanstack/react-query';
import { IQuestion, IQuestionAnswer } from '@/components/staffer/Grading/types';
import QuestionCard from '@/components/staffer/Grading/QuestionCards/QuestionCard';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import moment from 'moment-timezone';

interface ModelQuestionsContainerProps {
  isExpanded: boolean;
  modelId: string;
  modelType: string;
  questions: IQuestion[];
  questionAnswers: IQuestionAnswer[];
  toggleExpanded: (keys: string[]) => void;
}

const ModelQuestionsContainer = ({
  isExpanded,
  modelId,
  modelType,
  questionAnswers,
  questions,
  toggleExpanded,
} : ModelQuestionsContainerProps) => {
  const { student } = useGradingStore();

  const parentAttrsPath = () => {
    switch (modelType) {
    case 'Notebook':
      return plato_api_notebook_path(modelId);
    case 'Section':
      return plato_api_section_path(modelId);
    case 'SlideShow':
      return plato_api_slide_show_path(modelId);
    case 'ReadingGame':
      return plato_api_reading_game_path(modelId);
    case 'SlideGroup':
      return plato_api_slide_group_path(modelId);
    default:
      console.log(`no path for ${modelType} given`);
    }
  };

  const parentAttrsQuery = useQuery(['parentAttrs', modelId], () => (
    Axios.get(parentAttrsPath()).then(res => res.data)
  ));

  const turnedInQuery = useQuery([student.id, modelId], () => {
    return Axios.get(plato_api_find_exercise_result_for_grading_page_exercise_results_path({
      exercise_id: modelId,
      exercise_type: modelType,
      student_id: student.id,
    }));
  });

  const questionIds = questions.map(q => q.id);
  const numAnswered = questionAnswers?.filter(qa => questionIds.includes(qa.question_id)).length || 0;
  const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

  const title = () => {
    if (parentAttrsQuery.isLoading) return 'Loading...';
    let modelName = '';
    let sectionHeaderAttrName;
    switch (modelType) {
    case 'Notebook':
      sectionHeaderAttrName = 'section_header';
      modelName = ' - Notes';
      break;
    case 'ReadingGame':
      sectionHeaderAttrName = 'section_header';
      modelName = ' - Check';
      break;
    case 'SlideGroup':
      sectionHeaderAttrName = 'sectionHeader';
      break;
    default:
    }

    return `${parentAttrsQuery.data[Object.keys(parentAttrsQuery.data)[0]][sectionHeaderAttrName]}${modelName}`;
  };

  return (
    <div className={styles.card}>
      <div
        className={styles.modelQuestionsContainer}
        onClick={() => toggleExpanded([`${modelType}-${modelId}`])}
      >
        <i className={`fa ${isExpanded ? 'fa-caret-down' : 'fa-caret-right'}`} />
        <div>
          {title()}
        </div>
        { !turnedInQuery.isLoading && turnedInQuery.data?.data?.completed_at
          && (
            <div className={styles.turnInDate}>
              Turned in on {moment(turnedInQuery.data.data.completed_at).format('MMMM D, h:mm A')} {timezone}
            </div>
          )
        }
        <div className={styles.questionsAnswered}>
          {numAnswered} / {questions.filter(q => q.type !== 'PromptOnly').length} questions answered
        </div>
      </div>
      {isExpanded
        && (
          <div className={styles.bodyWrapper}>
            {questions.map((q) => {
              const questionAnswer = questionAnswers?.find(qa => qa.question_id === q.id);

              return (
                <QuestionCard
                  key={q.id}
                  question={q}
                  questionAnswer={questionAnswer}
                />
              );
            })}
          </div>
        )
      }
    </div>
  );
};

export default ModelQuestionsContainer;
